
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  BookOpen, 
  Heart, 
  CheckCircle2, 
  ShieldCheck,
  AlertTriangle
} from "lucide-react";
import ScriptureReferencesSection from "@/components/MoralDilemmas/ScriptureReferencesSection";
import { MoralDilemmasResponse } from "./useMoralDilemmas";
import ResponseActions from "@/components/MoralDilemmas/ResponseActions";

interface ResponseDisplayProps {
  question: string;
  response: MoralDilemmasResponse;
  isSaving: boolean;
  onSave: () => Promise<void>;
  onShareEmail: () => void;
  onShareSMS: () => void;
  onShareFacebook: () => void;
  onNewQuestion: () => void;
}

const ResponseDisplay: React.FC<ResponseDisplayProps> = ({
  question,
  response,
  isSaving,
  onSave,
  onShareEmail,
  onShareSMS,
  onShareFacebook,
  onNewQuestion
}) => {
  return (
    <div id="response-section" className="space-y-6 mb-12 mt-8">
      <h2 className="text-xl font-semibold text-[#403E43] mb-4 border-b border-heaven-gold/20 pb-2">
        Biblical Response to Your Question
      </h2>
      
      {/* Stack cards vertically (one on top of another) */}
      <div className="space-y-6">
        {/* Direct Answer */}
        <Card className="border-heaven-gold/10 w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-heaven-gold" />
              <span className="text-heaven-gold">Direct Answer</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#403E43]">{response.directAnswer}</p>
            </div>
          </CardContent>
        </Card>

        {/* Biblical Foundation */}
        <Card className="border-heaven-gold/10 w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-heaven-gold" />
              <span className="text-heaven-gold">Biblical Foundation</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-lg max-w-none">
              <p className="text-[#403E43]">{response.biblicalFoundation}</p>
            </div>
          </CardContent>
        </Card>

        {/* Scripture References */}
        {response.scriptureReferences && response.scriptureReferences.length > 0 ? (
          <div className="w-full">
            <ScriptureReferencesSection scriptureReferences={response.scriptureReferences} />
          </div>
        ) : null}

        {/* Cultural Contrast (always shown) */}
        <Card className="border-heaven-gold/10 w-full">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-heaven-gold" />
              <span className="text-heaven-gold">Cultural Contrast</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <p className="text-[#403E43]">{response.culturalContrast}</p>
            </div>
          </CardContent>
        </Card>

        {/* Personal Encouragement */}
        <Card className="border-heaven-gold/10 w-full">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Heart className="h-5 w-5 text-heaven-gold" />
              <span className="text-heaven-gold">Personal Encouragement</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <p className="text-[#403E43]">{response.personalEncouragement}</p>
            </div>
          </CardContent>
        </Card>

        {/* Call to Faithfulness */}
        <Card className="border-heaven-gold/10 w-full">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-heaven-gold" />
              <span className="text-heaven-gold">Call to Faithfulness</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <p className="text-[#403E43]">{response.callToFaithfulness}</p>
            </div>
          </CardContent>
        </Card>

        {/* Practical Steps */}
        {response.practicalSteps && response.practicalSteps.length > 0 && (
          <Card className="border-heaven-gold/10 w-full">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-heaven-gold" />
                <span className="text-heaven-gold">Practical Steps</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {response.practicalSteps.map((step, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-heaven-gold mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{step}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      <ResponseActions 
        onSave={onSave}
        onShareEmail={onShareEmail}
        onShareSMS={onShareSMS}
        onShareFacebook={onShareFacebook}
        isSaving={isSaving}
      />

      <div className="flex justify-center mt-4">
        <Button
          variant="outline"
          className="border-heaven-gold/30 text-heaven-gold hover:bg-heaven-gold/10"
          onClick={onNewQuestion}
        >
          New Question
        </Button>
      </div>
    </div>
  );
};

export default ResponseDisplay;
